import React from "react";
import { Container, Paper, Box, Typography, Alert } from "@mui/material";
import { CardSkeleton } from "../common/SkeletonLoaders";
import { themeColors } from "../../utils/theme";
import { useAuth } from "../../contexts/AuthContext";
import { useS3Service } from "../../hooks/useS3Service";
import {
  S3BrowserProvider,
  useS3Browser,
} from "../../contexts/S3BrowserContext";
import { S3FileBrowserProps } from "../../types/s3";

import S3FileTable from "./S3FileTable";
import S3BreadcrumbNav from "./S3BreadcrumbNav";
import S3ErrorBoundary from "./S3ErrorBoundary";
import { S3DragDropZone } from "./S3DragDropZone";

/**
 * S3FileBrowserContent Component (Internal)
 *
 * Internal component that uses S3Browser context for state management
 *
 * @returns {JSX.Element} Rendered S3 file browser content
 */
const S3FileBrowserContent: React.FC = () => {
  // Get authentication state
  const { currentUser } = useAuth();

  // Get S3 service state and authentication status
  const {
    isAuthenticated,
    error: authError,
    loading: authLoading,
  } = useS3Service();

  // Get S3 browser state and actions from context
  const { state, navigateTo, filteredReports } = useS3Browser();

  /**
   * Renders access denied view for unauthenticated users
   */
  if (!currentUser) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              bgcolor: themeColors.paper,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{ mb: 3, color: themeColors.text }}
            >
              Access Denied
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              You must be logged in to access the Reports Download section
            </Alert>
            <Typography
              variant="body2"
              sx={{ color: themeColors.textSecondary }}
            >
              Please ensure you are authenticated to view and download files.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  /**
   * Show loading state while checking S3 authentication
   */
  if (authLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <CardSkeleton lines={3} hasImage={false} />
        </Box>
      </Container>
    );
  }

  /**
   * Show error state if S3 authentication fails
   */
  if (!isAuthenticated || authError) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              bgcolor: themeColors.paper,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{ mb: 3, color: themeColors.text }}
            >
              Storage Access Unavailable
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              Unable to establish secure connection to file storage
            </Alert>
            {authError && (
              <Typography
                variant="body2"
                sx={{ color: themeColors.textSecondary, mb: 2 }}
              >
                Error: {authError}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{ color: themeColors.textSecondary }}
            >
              Please check your connection and try again. If the problem
              persists, contact support.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  /**
   * Main S3 file browser interface for authenticated users
   */
  return (
    <>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Main content paper */}
          <Paper
            elevation={3}
            sx={{
              bgcolor: themeColors.paper,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {/* Navigation breadcrumbs */}
            <Box
              sx={{
                p: 3,
                pb: 0,
                borderBottom: `1px solid ${themeColors.accent}`,
              }}
            >
              <S3BreadcrumbNav />
            </Box>

            {/* File listing table */}
            <Box sx={{ p: 3 }}>
              <S3FileTable />
            </Box>
          </Paper>

          {/* TODO: FEATURE-CHECKBOX-SELECTION [REMOVED] */}
          {/* Description: Selection summary with hidden items indicator */}
          {/* Restore: Uncomment the entire Paper block below to restore selection summary */}
          {/* Ref: docs/feature-plans/checkbox-selection-removal-plan.md */}
          {/* Selection summary with hidden items indicator */}
          {/* {state.selection.selectionCount > 0 && (
            <Box sx={{ mt: 3 }}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  bgcolor: `${themeColors.primary}20`,
                  border: `1px solid ${themeColors.primary}40`,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: themeColors.text, mb: 1 }}
                >
                  <strong>{state.selection.selectionCount}</strong>{" "}
                  {state.selection.selectionCount === 1 ? "item" : "items"}{" "}
                  selected
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: themeColors.textSecondary, mb: 1 }}
                >
                  {state.selection.selectedFiles.length} files,{" "}
                  {state.selection.selectedFolders.length} folders
                </Typography>
                {state.selection.selectedItems.length > 0 &&
                  state.selection.selectedItems.filter(
                    (item) => !filteredReports?.find((r) => r.key === item.key)
                  ).length > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {
                        state.selection.selectedItems.filter(
                          (item) =>
                            !filteredReports?.find((r) => r.key === item.key)
                        ).length
                      }{" "}
                      selected item(s) are not visible in the current filtered
                      view but will be included in bulk downloads.
                    </Alert>
                  )}
              </Paper>
            </Box>
          )} */}

          {/* Footer info */}
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{ color: themeColors.textSecondary }}
            >
              Secure file access • Files are downloaded using time-limited
              signed URLs
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Context-aware drag-drop zone for this S3 browser instance */}
      <S3DragDropZone />
    </>
  );
};

/**
 * S3FileBrowser Component
 *
 * Main entry point for the S3 file browser interface with context provider.
 * Provides secure access to S3 bucket contents with hierarchical navigation,
 * file listing, and download capabilities.
 *
 * **NOTE: Checkbox selection is currently disabled.** The underlying selection state 
 * management remains in S3BrowserContext for future re-enablement.
 *
 * Supports two modes:
 * 1. Standalone mode: Component manages its own data fetching (legacy behavior)
 * 2. Controlled mode: Parent provides filtered data and manages loading/error states
 *
 * Features:
 * - Hierarchical folder navigation with breadcrumbs
 * - File and folder listing using DataGrid
 * - Secure file downloads with signed URLs
 * - Authentication-required access
 * - Responsive Material-UI design
 * - Centralized state management via React Context
 * - Filter integration support
 *
 * @see docs/feature-plans/checkbox-selection-removal-plan.md
 * @param {S3FileBrowserProps} props - Component props
 * @returns {JSX.Element} Rendered S3 file browser interface with context
 */

const S3FileBrowser: React.FC<S3FileBrowserProps> = ({
  basePath,
  filteredReports,
  isLoading,
  error,
  paginationMetadata,
  onPageChange,
  onPageSizeChange,
}) => {
  return (
    <S3ErrorBoundary>
      <S3BrowserProvider
        initialPath={basePath}
        filteredReports={filteredReports}
        isLoading={isLoading}
        error={error}
        paginationMetadata={paginationMetadata}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <S3FileBrowserContent />
      </S3BrowserProvider>
    </S3ErrorBoundary>
  );
};

export default S3FileBrowser;
